function number(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

export function average(rows, field) {
  if (!Array.isArray(rows) || rows.length === 0) {
    return null;
  }

  const values = rows
    .map((row) => Number(row?.[field]))
    .filter(Number.isFinite);

  if (!values.length) {
    return null;
  }

  return (
    values.reduce((sum, value) => sum + value, 0) /
    values.length
  );
}

function recalculateFromTotalGoals(row, totalGoals) {
  const totalStrength = number(row.total_strength, 1);

  const againstTotal =
    totalGoals / (totalStrength + 1);

  const forTotal =
    totalGoals - againstTotal;

  const forHomeFactor =
    number(row.for_homefactor, 1);

  const againstHomeFactor =
    number(row.against_homefactor, 1);

  return {
    ...row,
    total_goals: totalGoals,
    against_total: againstTotal,
    for_total: forTotal,
    for_home: forTotal * forHomeFactor,
    for_away: forTotal * (2 - forHomeFactor),
    against_home:
      againstTotal * againstHomeFactor,
    against_away:
      againstTotal * (2 - againstHomeFactor),
    isNew: false,
  };
}

export function setGoalAverage(rows, target) {
  const targetValue = number(target);

  return rows.map((row) =>
    recalculateFromTotalGoals(
      row,
      targetValue
    )
  );
}

export function adjustGoalAverage(rows, target) {
  const current =
    average(rows, "total_goals");

  if (!current || current <= 0) {
    throw new Error(
      "Der aktuelle Torschnitt konnte nicht bestimmt werden."
    );
  }

  const factor = number(target) / current;

  return rows.map((row) =>
    recalculateFromTotalGoals(
      row,
      number(row.total_goals) * factor
    )
  );
}

function recalculateHomeValues(
  row,
  forHomeFactor,
  againstHomeFactor
) {
  const forTotal = number(row.for_total);
  const againstTotal =
    number(row.against_total);

  return {
    ...row,
    for_homefactor: forHomeFactor,
    against_homefactor:
      againstHomeFactor,

    for_home:
      forTotal * forHomeFactor,

    for_away:
      forTotal * (2 - forHomeFactor),

    against_home:
      againstTotal * againstHomeFactor,

    against_away:
      againstTotal *
      (2 - againstHomeFactor),

    isNew: false,
  };
}

export function setHomeAdvantage(
  rows,
  target
) {
  const forTarget = number(target);
  const againstTarget = 2 - forTarget;

  return rows.map((row) =>
    recalculateHomeValues(
      row,
      forTarget,
      againstTarget
    )
  );
}

export function adjustHomeAdvantage(
  rows,
  target
) {
  const currentFor =
    average(rows, "for_homefactor");

  const currentAgainst =
    average(rows, "against_homefactor");

  if (
    !currentFor ||
    !currentAgainst ||
    currentFor <= 0 ||
    currentAgainst <= 0
  ) {
    throw new Error(
      "Die aktuellen Heimfaktoren konnten nicht bestimmt werden."
    );
  }

  const targetFor = number(target);
  const targetAgainst = 2 - targetFor;

  const forFactor =
    targetFor / currentFor;

  const againstFactor =
    targetAgainst / currentAgainst;

  return rows.map((row) =>
    recalculateHomeValues(
      row,
      number(row.for_homefactor) *
        forFactor,

      number(row.against_homefactor) *
        againstFactor
    )
  );
}

export function setDrawFactor(rows, target) {
  const targetValue = number(target);

  return rows.map((row) => ({
    ...row,
    draw_factor: targetValue,
    isNew: false,
  }));
}

export function adjustDrawFactor(
  rows,
  target
) {
  const current =
    average(rows, "draw_factor");

  if (!current || current <= 0) {
    throw new Error(
      "Der aktuelle Unentschieden-Faktor konnte nicht bestimmt werden."
    );
  }

  const factor = number(target) / current;

  return rows.map((row) => ({
    ...row,
    draw_factor:
      number(row.draw_factor) * factor,
    isNew: false,
  }));
}

export function calculateRecommendation({
  storedValue,
  observedValue,
  sampleSize,
  fullWeightAt = 30,
}) {
  const stored = Number(storedValue);
  const observed = Number(observedValue);
  const sample = Number(sampleSize);
  const fullWeight = Number(fullWeightAt);

  if (
    !Number.isFinite(stored) ||
    !Number.isFinite(observed) ||
    !Number.isFinite(sample) ||
    !Number.isFinite(fullWeight) ||
    fullWeight <= 0
  ) {
    return null;
  }

  const weight = Math.max(
    0,
    Math.min(1, sample / fullWeight)
  );

  return (
    stored +
    weight * (observed - stored)
  );
}


export function normalizeGoalTotals(rows) {
  if (!Array.isArray(rows) || rows.length === 0) {
    throw new Error(
      "Es sind keine Team Settings vorhanden."
    );
  }

  const sumFor = rows.reduce(
    (sum, row) =>
      sum + number(row.for_total),
    0
  );

  const sumAgainst = rows.reduce(
    (sum, row) =>
      sum + number(row.against_total),
    0
  );

  if (sumFor <= 0 || sumAgainst <= 0) {
    throw new Error(
      "For Total oder Against Total kann nicht normiert werden."
    );
  }

  const target =
    (sumFor + sumAgainst) / 2;

  const forFactor =
    target / sumFor;

  const againstFactor =
    target / sumAgainst;

  return rows.map((row) => {
    const forTotal =
      number(row.for_total) * forFactor;

    const againstTotal =
      number(row.against_total) *
      againstFactor;

    const forHomeFactor =
      number(row.for_homefactor, 1);

    const againstHomeFactor =
      number(row.against_homefactor, 1);

    return {
      ...row,

      for_total: forTotal,
      against_total: againstTotal,

      total_goals:
        forTotal + againstTotal,

      total_strength:
        againstTotal !== 0
          ? forTotal / againstTotal
          : number(row.total_strength),

      for_home:
        forTotal * forHomeFactor,

      for_away:
        forTotal *
        (2 - forHomeFactor),

      against_home:
        againstTotal *
        againstHomeFactor,

      against_away:
        againstTotal *
        (2 - againstHomeFactor),

      isNew: false,
    };
  });
}

export function normalizeHomeAdvantage(rows) {
  if (!Array.isArray(rows) || rows.length === 0) {
    throw new Error(
      "Es sind keine Team Settings vorhanden."
    );
  }

  const averageFor =
    average(rows, "for_homefactor");

  const averageAgainst =
    average(rows, "against_homefactor");

  if (
    !Number.isFinite(averageFor) ||
    !Number.isFinite(averageAgainst) ||
    averageFor <= 0 ||
    averageAgainst <= 0
  ) {
    throw new Error(
      "Der Heimvorteil kann nicht normiert werden."
    );
  }

  const currentSum =
    averageFor + averageAgainst;

  if (currentSum <= 0) {
    throw new Error(
      "Die Summe der Heimfaktoren ist ungültig."
    );
  }

  /*
   * Beide Faktorgruppen werden mit demselben
   * Faktor skaliert. Dadurch bleiben ihre
   * Relationen erhalten, während die Summe
   * ihrer Mittelwerte exakt 2,000 wird.
   */
  const factor = 2 / currentSum;

  return rows.map((row) => {
    const forHomeFactor =
      number(row.for_homefactor, 1) *
      factor;

    const againstHomeFactor =
      number(row.against_homefactor, 1) *
      factor;

    const forTotal =
      number(row.for_total);

    const againstTotal =
      number(row.against_total);

    return {
      ...row,

      for_homefactor:
        forHomeFactor,

      against_homefactor:
        againstHomeFactor,

      for_home:
        forTotal * forHomeFactor,

      for_away:
        forTotal *
        (2 - forHomeFactor),

      against_home:
        againstTotal *
        againstHomeFactor,

      against_away:
        againstTotal *
        (2 - againstHomeFactor),

      isNew: false,
    };
  });
}
